import { Component, EventEmitter, Input, Output, OnChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { PlanFormValue } from '../../../../core/models/plan.model';
import { UiButtonComponent } from '../../../../shared/ui-button/ui-button';
import { FORM_LIMITS } from '../../../../core/utils/validation.utils';

interface FormModel {
  code: string;
  name: string;
  description: string;
  maxAccounts: string;
  advancedReports: boolean;
  isActive: boolean;
}

@Component({
  selector: 'app-plan-form',
  standalone: true,
  imports: [FormsModule, UiButtonComponent],
  templateUrl: './plan-form.html',
  styleUrl: './plan-form.css',
})
export class PlanFormComponent implements OnChanges {
  @Input() plan: PlanFormValue | null = null;
  @Input() serverError = '';
  @Output() readonly saved = new EventEmitter<PlanFormValue>();
  @Output() readonly cancelled = new EventEmitter<void>();

  protected readonly form: FormModel = this.emptyForm();
  protected error = '';

  ngOnChanges(): void {
    this.loadPlan();
  }

  protected submit(): void {
    if (!this.form.code.trim() || !this.form.name.trim()) {
      this.error = 'El código y el nombre son obligatorios.';
      return;
    }
    if (
      !/^[A-Z0-9_-]{2,30}$/.test(this.form.code.trim().toUpperCase()) ||
      this.form.name.trim().length > FORM_LIMITS.nameMaxLength ||
      this.form.description.trim().length > FORM_LIMITS.descriptionMaxLength
    ) {
      this.error = 'Revisa el formato y la longitud de los datos del plan.';
      return;
    }

    const maxAccounts = Number(this.form.maxAccounts);
    if (!Number.isInteger(maxAccounts) || maxAccounts < -1) {
      this.error =
        'El máximo de cuentas debe ser un número entero mayor o igual a 0. Usa -1 para ilimitadas.';
      return;
    }

    this.error = '';
    this.saved.emit({
      id: this.plan?.id,
      code: this.form.code.trim().toUpperCase(),
      name: this.form.name.trim(),
      description: this.form.description.trim() || null,
      characteristicsJson: JSON.stringify({
        maxCuentas: maxAccounts,
        reportesAvanzados: this.form.advancedReports,
      }),
      isActive: this.form.isActive,
    });
  }

  private loadPlan(): void {
    const characteristics = this.parseCharacteristics(this.plan?.characteristicsJson);
    const maxCuentasValue = characteristics['maxCuentas'];
    const maxCuentas =
      typeof maxCuentasValue === 'number' && Number.isFinite(maxCuentasValue) ? maxCuentasValue : 0;
    Object.assign(this.form, {
      code: this.plan?.code ?? '',
      name: this.plan?.name ?? '',
      description: this.plan?.description ?? '',
      maxAccounts: String(maxCuentas),
      advancedReports: characteristics['reportesAvanzados'] === true,
      isActive: this.plan?.isActive ?? true,
    });
    this.error = '';
  }

  private parseCharacteristics(value: string | undefined): Record<string, unknown> {
    try {
      return value ? (JSON.parse(value) as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }

  private emptyForm(): FormModel {
    return {
      code: '',
      name: '',
      description: '',
      maxAccounts: '0',
      advancedReports: false,
      isActive: true,
    };
  }
}
